import BlogLayout from "../components/BlogLayout";
import { getPostBySlug } from "../config/blogPosts";

const FAQS = [
  {
    q: "Is a dummy ticket legal?",
    a: "A genuine dummy ticket is a temporary flight reservation used for visa documentation or proof of onward travel. It should have a real booking reference or PNR and should be verifiable while active. What you should avoid is a fake, edited, or forged ticket.",
  },
  {
    q: "Is a dummy ticket the same as a fake ticket?",
    a: "No. A proper dummy ticket is a real temporary reservation. A fake ticket is a manually edited PDF or a made-up booking that does not exist in an airline system. If the PNR cannot be verified, do not submit it.",
  },
  {
    q: "Can embassies or immigration officers verify a dummy ticket?",
    a: "A valid reservation can usually be checked through the airline’s “Manage Booking” page or relevant booking system while it is active. That is why the PNR, passenger name, route, and travel dates should all match your visa application or travel documents.",
  },
  {
    q: "Do I need to buy a confirmed flight ticket before visa approval?",
    a: "Not always. Many visa applicants use a temporary flight reservation because buying a fully paid ticket before visa approval can be financially risky. Always follow the latest checklist for the country you are applying to, especially if it specifically asks for a paid or confirmed ticket.",
  },
];

export default function BlogPostDummyTicket() {
  const post = getPostBySlug("what-is-a-dummy-ticket");

  return (
    <BlogLayout
      post={post}
      breadcrumbListTitle="What Is a Dummy Ticket?"
      faqs={FAQS}
      ctaHeading="Need a temporary flight reservation for your visa application?"
      ctaButtonLabel="Book reservation"
    >
      <p>Every week, someone asks us some version of the same question.</p>
      <p>
        "I just paid for my dummy ticket. Is this actually legitimate? I'm
        nervous."
      </p>
      <p>
        We've been answering that question for over ten years. And honestly,
        the nervousness makes complete sense. The name doesn't help. "Dummy
        ticket" sounds like something you'd find on a shady forum, not
        something you'd submit to a German consulate or hand to an immigration
        officer at Suvarnabhumi.
      </p>
      <p>So let's clear this up properly, once and for all.</p>

      <h2>What a Dummy Ticket Actually Is</h2>
      <p>
        A dummy ticket is a confirmed flight reservation with a real PNR
        (Passenger Name Record) that exists in an airline's booking system. It
        is not a fabricated document. It is not a Photoshopped PDF. It is a
        genuine booking made through the same global distribution system (GDS)
        that every travel agent in the world uses.
      </p>
      <p>
        The only difference between this and a ticket you buy for an actual
        trip is that it hasn't been paid in full and will be cancelled before
        the departure date.
      </p>
      <p>That's it. That's the whole thing.</p>
      <p>
        When a visa officer at the French consulate or an immigration officer
        at Bangkok airport asks to see proof of onward travel, what they want
        to verify is that you have a travel plan. They check your PNR on the
        airline's own website. If your name comes up with a valid route and
        date, it passes. Whether you ultimately fly that specific itinerary is
        not their concern at that stage.
      </p>
      <p>
        We've processed reservations across more than 140 countries. The
        reservations work because they're real bookings, not because we found
        a clever workaround.
      </p>

      <h2>Why People Need This in the First Place</h2>
      <p>
        This surprises some travelers: most visa applications and many
        immigration checkpoints require you to show proof of travel before you
        have any certainty you'll be allowed to travel.
      </p>
      <p>Think about that for a moment.</p>
      <p>
        To apply for a Schengen visa, you need to show flight bookings. But
        you haven't been approved yet. Buying a fully paid, non-refundable
        ticket before your visa decision is a real financial risk. Airlines
        don't issue refunds because your visa was rejected.
      </p>
      <p>
        Priya from Hyderabad put it well in a message she sent us last year:
        "I had applied for my France visa twice before and both times I bought
        real tickets that I had to cancel when the visa got delayed. The
        third time I found you and spent ₹800 instead of risking ₹45,000. I
        don't know why nobody told me about this earlier."
      </p>
      <p>
        Priya is not unusual. This is exactly the situation dummy tickets
        exist to solve.
      </p>
      <p>
        The same logic applies to digital nomads with open-ended itineraries,
        travelers going through immigration at high-scrutiny airports, and
        anyone who wants a flexible travel plan without committing to a
        non-refundable booking before they're ready.
      </p>

      <h2>Is It Legal?</h2>
      <p>Yes. With one important distinction.</p>
      <p>
        A legitimate flight reservation from a verified GDS system is
        completely legal. It is the standard industry practice for visa
        applications worldwide. Embassies know this exists. Immigration
        authorities know this exists. Many visa consultants actively recommend
        it.
      </p>
      <p>
        What is not legal is a forged document. A PDF that someone edited in
        Canva with a made-up PNR. A booking that doesn't actually exist in any
        airline system. That's fraud, and it takes an immigration officer
        approximately 30 seconds to spot it.
      </p>
      <p>The difference is verifiability.</p>
      <p>
        Every reservation we issue can be checked directly on the airline's
        website using the PNR. Your name, your route, your travel date, all
        visible exactly as they should be. That's what makes it legitimate.
        The moment a PNR can't be verified on the airline's own
        manage-booking page, you're no longer looking at a dummy ticket.
        You're looking at a fake document.
      </p>
      <p>
        We've had customers come to us after buying a ₹150 "dummy ticket" from
        a WhatsApp number. The PNR didn't resolve on the airline website.
        They came to us two days before their visa appointment, stressed and
        scrambling. It's one of the most avoidable situations we see.
      </p>

      <h2>How Embassies and Immigration Officers Actually Verify</h2>
      <p>
        This is the part most people are curious about and most articles skip
        over.
      </p>
      <p>
        Visa officers do not call the airline. They don't have a special
        database or insider system. They open the same airline website you
        use to check in or select a seat, go to "Manage Booking," and enter
        your PNR. If the booking appears with your name and travel details,
        it clears.
      </p>
      <p>
        Immigration officers at airports follow the same process, often
        faster. At busy airports like Bangkok, Bali, or Kuala Lumpur, where
        onward travel checks are common, the officer typically checks while
        you're still at the counter.
      </p>
      <p>
        This is why the source of your reservation matters more than the
        price you paid for it. A reservation issued through a proper GDS
        system resolves cleanly and consistently. A reservation that was
        manually fabricated doesn't resolve at all.
      </p>
      <p>
        One of our long-term customers, a freelance consultant who travels
        through Southeast Asia roughly eight months a year, told us he's had
        his reservation checked at immigration four times across different
        countries. "It has never once failed. I just hand them the PDF and
        they check it and wave me through. It's less drama than showing my
        actual passport."
      </p>

      <h2>The One Thing to Check Before You Buy From Anyone</h2>
      <p>Whoever you buy from, do this before you pay:</p>
      <p>
        Ask which airline and which GDS system your reservation will be
        issued through. Any legitimate provider will tell you immediately.
        Then, once you receive your PNR, go directly to that airline's website
        and enter it in the manage booking section.
      </p>
      <p>
        Your name should appear. Your route should appear. Your travel date
        should appear.
      </p>
      <p>
        If it does, you have a legitimate reservation. If it doesn't, you
        have a problem.
      </p>
      <p>
        This check takes two minutes and it's the only quality test that
        matters.
      </p>

      <h2>A Note on the Name</h2>
      <p>
        We've thought about this more than you'd expect. "Dummy ticket" is an
        industry term that has stuck around because it's specific and
        everyone in travel knows what it means. But we understand why it
        creates hesitation for first-time buyers. It sounds unofficial
        because it sounds made-up.
      </p>
      <p>
        The more accurate term is "flight reservation" or "flight itinerary."
        Some services use "onward ticket." All of these refer to the same
        thing: a verified, GDS-issued booking that serves as proof of travel
        without requiring full payment upfront.
      </p>
      <p>
        Whatever you call it, the legitimacy comes from one thing: whether
        the PNR resolves on the airline's website. Everything else is just
        branding.
      </p>

      <h2>The Bottom Line</h2>
      <p>
        A dummy ticket is a real flight reservation issued through a
        legitimate booking system. It is legal, widely accepted by embassies
        and immigration authorities, and exists specifically because the
        travel and visa process requires proof of a plan before that plan
        can be confirmed.
      </p>
      <p>
        The risk is not in using one. The risk is in buying one from a
        source that can't actually produce a verifiable booking.
      </p>
      <p>
        If you're preparing a visa application or heading to an airport
        where onward travel proof is required, we've been doing this for
        over ten years. We know what works, what doesn't, and exactly what
        a consulate or immigration officer needs to see.
      </p>
      <p>You can get your reservation in under 15 minutes. And yes, it will verify.</p>

      <p className="gd-article-note">
        Need a temporary flight reservation for your visa application?
        GetDeparto provides verifiable flight reservations with live PNR
        details, delivered on WhatsApp and email.
      </p>

      <h2>FAQs</h2>
      <div className="gd-article-faqs" data-testid="article-faqs">
        {FAQS.map((f, i) => (
          <div className="gd-article-faq" key={i} data-testid={`article-faq-${i}`}>
            <h3 className="gd-article-faq-q">{`${i + 1}. ${f.q}`}</h3>
            <p className="gd-article-faq-a">{f.a}</p>
          </div>
        ))}
      </div>
    </BlogLayout>
  );
}
